import type { components } from "../generated-schemas/client-portal";

function formatMessage(status: number, statusText: string) {
  return `Request failed with status ${statusText || status}.`;
}

export type RegionEntitlementDeniedBody =
  components["schemas"]["ClientPortalRegionEntitlementDenied"];

const SKIP_KEYS = new Set(["detail", "non_field_errors", "error"]);
const EMPTY_FIELD = /^this field (may not be (null|blank)|is required)\.?$/i;

export type ParseErrorOptions = {
  fieldLabels?: Record<string, string>;
  formatEmptyFields?: (labels: string[]) => string;
};

function flatten(value: unknown, path = ""): [string, string][] {
  if (typeof value === "string") {
    return path ? [[path, value]] : [];
  }
  if (Array.isArray(value)) {
    return value.flatMap((item) => flatten(item, path));
  }
  if (value && typeof value === "object") {
    return Object.entries(value).flatMap(([key, nested]) =>
      flatten(nested, path ? `${path}.${key}` : key)
    );
  }
  return [];
}

function fieldLabel(path: string, extra: Record<string, string> = {}) {
  const leaf = path.split(".").at(-1) ?? path;
  const fallback = path.replace(/[._]/g, " ");
  return (
    extra[path] ??
    extra[leaf] ??
    fallback.charAt(0).toUpperCase() + fallback.slice(1)
  );
}

function joinErrorMessages(value: unknown) {
  if (typeof value === "string") {
    return value;
  }
  if (Array.isArray(value)) {
    return value
      .map((message) =>
        typeof message === "string" ? message : JSON.stringify(message)
      )
      .join(", ");
  }
}

function formatFieldErrors(
  body: Record<string, unknown>,
  options?: ParseErrorOptions
) {
  const pairs = Object.entries(body)
    .filter(([key]) => !SKIP_KEYS.has(key))
    .flatMap(([key, value]) => flatten(value, key));
  if (pairs.length === 0) {
    return;
  }

  const extra = options?.fieldLabels;
  const empty = pairs.filter(([, message]) => EMPTY_FIELD.test(message));
  const other = pairs.filter(([, message]) => !EMPTY_FIELD.test(message));
  const labelled = (path: string, message: string) =>
    `${fieldLabel(path, extra)}: ${message}`;

  return [
    empty.length && options?.formatEmptyFields
      ? options.formatEmptyFields(
          empty.map(([path]) => fieldLabel(path, extra))
        )
      : empty.map(([path, message]) => labelled(path, message)).join(" "),
    ...other.map(([path, message]) => labelled(path, message)),
  ]
    .filter(Boolean)
    .join(" ");
}

// TODO: Expand this once we have more standard errors/ error messages
export class APIError extends Error {
  status: number;
  statusText: string;
  body: Record<string, unknown> = {};
  detail: string;

  constructor(status: number, statusText: string, body: unknown) {
    super(formatMessage(status, statusText));

    this.status = status;
    this.statusText = statusText;
    this.message = formatMessage(status, statusText);
    this.detail = this.message;

    if (body !== null && typeof body === "object") {
      this.body = body as Record<string, unknown>;

      if (typeof this.body.detail === "string") {
        this.detail = this.body.detail;
      }

      if (typeof this.body.non_field_errors === "string") {
        this.detail = this.body.non_field_errors as string;
      }
    }

    if (typeof body === "string") {
      this.detail = body;
    }
  }
}

export function defaultParseError(
  error: APIError,
  options?: ParseErrorOptions
) {
  return (
    joinErrorMessages(error.body.detail) ??
    joinErrorMessages(error.body.non_field_errors) ??
    joinErrorMessages(error.body.error) ??
    formatFieldErrors(error.body, options)
  );
}

export function defaultParseFieldErrors(error: APIError) {
  return error.body as Record<string, unknown>;
}

export function isRegionRestrictedError(error: APIError): boolean {
  return error instanceof APIError && error.status === 451;
}

export function isRegionEntitlementDeniedError(
  error: unknown
): error is APIError & { body: RegionEntitlementDeniedBody } {
  return (
    error instanceof APIError &&
    error.status === 403 &&
    error.body?.code === "region_entitlement_denied"
  );
}

export class ResponseParseError extends Error {
  status: number;
  requestId: string | null;

  constructor(opts: {
    status: number;
    requestId: string | null;
    cause: unknown;
  }) {
    super(`Unable to parse API response (HTTP ${opts.status})`);
    this.status = opts.status;
    this.requestId = opts.requestId;
    if (opts.cause instanceof Error) {
      this.cause = opts.cause;
    }
  }
}

/**
 * Short, human-quotable form of the request ID (first 8 chars) for display in
 * an error toast. A CSM can read this back to engineering to correlate with the
 * backend Sentry event tagged with the same X-Request-Id.
 */
export function getShortRequestId(requestId: string | null): string | null {
  return requestId ? requestId.slice(0, 8) : null;
}

/**
 * Single source of truth for what is safe to send to Sentry for a
 * ResponseParseError. Deliberately excludes the raw response body: it is
 * unvalidated backend output that may contain applicant PII, and must not be
 * forwarded to a third-party SaaS. Only non-identifying correlation metadata
 * is included.
 */
export function getResponseParseErrorSentryExtra(error: ResponseParseError): {
  status: number;
  requestId: string | null;
  parseError: true;
} {
  return {
    status: error.status,
    requestId: error.requestId,
    parseError: true,
  };
}
